import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCamera, FaTimes } from 'react-icons/fa';

const EditProfileModal = ({
  show, user, editData, setEditData, onClose, onSave,
  avatarUploading, coverUploading, onAvatarUpload, onCoverUpload,
}) => (
  <AnimatePresence>
    {show && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimes /></button>
          </div>
          <div className="space-y-4">
            {/* Cover Photo */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Cover Photo</label>
              <div className="relative h-32 rounded-xl overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 hover:border-[#4A90E2]/50 transition-colors group">
                {(editData.cover_photo_url || user.cover_photo_url) ? (
                  <img src={editData.cover_photo_url || user.cover_photo_url} alt="Cover" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full"><FaCamera className="text-2xl text-gray-300" /></div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  {coverUploading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <div className="bg-white/90 rounded-full p-2.5"><FaCamera className="text-gray-700" size={14} /></div>}
                  <input type="file" accept="image/*" onChange={onCoverUpload} className="hidden" data-testid="cover-photo-upload" />
                </label>
              </div>
            </div>
            {/* Profile Photo */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Profile Photo</label>
              <div className="flex items-center space-x-4">
                <div className="relative group">
                  <img src={editData.avatar_url || user.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt="Avatar" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" data-testid="edit-profile-avatar" />
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    {avatarUploading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaCamera className="text-white" size={16} />}
                    <input type="file" accept="image/*" onChange={onAvatarUpload} className="hidden" data-testid="avatar-upload" />
                  </label>
                </div>
                <div className="text-sm text-gray-500"><p className="font-medium text-gray-700">Change photo</p><p className="text-xs">Click the image to upload</p></div>
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Full Name</label>
              <input type="text" defaultValue={user.full_name} onChange={e => setEditData(prev => ({ ...prev, full_name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]" />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Username</label>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#4A90E2]">
                <span className="px-3 py-3 bg-gray-50 text-gray-400 text-sm border-r border-gray-200">@</span>
                <input type="text" defaultValue={user.username} onChange={e => setEditData(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))} className="flex-1 px-3 py-3 focus:outline-none" data-testid="edit-username-input" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Only lowercase letters, numbers, and underscores</p>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Bio</label>
              <textarea defaultValue={user.bio} onChange={e => setEditData(prev => ({ ...prev, bio: e.target.value }))} rows={3} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4A90E2] resize-none" />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Location</label>
              <input type="text" defaultValue={user.location} onChange={e => setEditData(prev => ({ ...prev, location: e.target.value }))} placeholder="City, Country" className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]" />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Website</label>
              <input type="url" defaultValue={user.website_url} onChange={e => setEditData(prev => ({ ...prev, website_url: e.target.value }))} placeholder="https://yourwebsite.com" className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]" />
            </div>
          </div>
          <div className="flex space-x-3 mt-6">
            <button onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={onSave} className="flex-1 py-3 bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white rounded-lg hover:shadow-lg">Save Changes</button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default EditProfileModal;
